import { useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput,
  TouchableOpacity, Modal, Alert, Platform, KeyboardAvoidingView,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash2, ChevronUp, ChevronDown, X, Search } from 'lucide-react-native';
import {
  saveProgram,
  type Program,
  type ProgramDay,
  type ProgramExercise,
} from '@/lib/programBuilder';
import MobileExercisePicker from '@/components/MobileExercisePicker';
import { useExercises } from '@/hooks/useExercises';

interface Props {
  existingProgram?: Program;
  onClose: () => void;
  onSaved: () => void;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function makeEmptyExercise(): ProgramExercise {
  return { id: generateId(), exerciseName: '', targetSets: 3, targetReps: 8 };
}

function makeEmptyDay(index: number): ProgramDay {
  return {
    id: generateId(),
    name: `Day ${String.fromCharCode(65 + index)}`,
    exercises: [makeEmptyExercise()],
  };
}

const RPE_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export default function ProgramBuilder({ existingProgram, onClose, onSaved }: Props) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;

  const [name, setName] = useState(existingProgram?.name ?? '');
  const [days, setDays] = useState<ProgramDay[]>(
    existingProgram?.days ?? [makeEmptyDay(0)]
  );
  const [saving, setSaving] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<{ dayId: string; exId: string } | null>(null);

  const { exercises: exerciseList } = useExercises();

  const addDay = () => setDays([...days, makeEmptyDay(days.length)]);

  const removeDay = (dayId: string) => {
    if (days.length <= 1) return;
    setDays(days.filter((d) => d.id !== dayId));
  };

  const updateDayName = (dayId: string, newName: string) => {
    setDays(days.map((d) => (d.id === dayId ? { ...d, name: newName } : d)));
  };

  const addExercise = (dayId: string) => {
    setDays(
      days.map((d) =>
        d.id === dayId ? { ...d, exercises: [...d.exercises, makeEmptyExercise()] } : d
      )
    );
  };

  const removeExercise = (dayId: string, exId: string) => {
    setDays(
      days.map((d) =>
        d.id === dayId ? { ...d, exercises: d.exercises.filter((e) => e.id !== exId) } : d
      )
    );
  };

  const updateExercise = (dayId: string, exId: string, updates: Partial<ProgramExercise>) => {
    setDays(
      days.map((d) =>
        d.id === dayId
          ? {
              ...d,
              exercises: d.exercises.map((e) => (e.id === exId ? { ...e, ...updates } : e)),
            }
          : d
      )
    );
  };

  const moveExercise = (dayId: string, exId: string, direction: -1 | 1) => {
    setDays(
      days.map((d) => {
        if (d.id !== dayId) return d;
        const idx = d.exercises.findIndex((e) => e.id === exId);
        if (idx < 0) return d;
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= d.exercises.length) return d;
        const newExercises = [...d.exercises];
        [newExercises[idx], newExercises[newIdx]] = [newExercises[newIdx], newExercises[idx]];
        return { ...d, exercises: newExercises };
      })
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      const msg = 'Please enter a program name.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Missing Name', msg);
      return;
    }
    if (!profile?.id) return;

    setSaving(true);
    try {
      const program: Program = {
        id: existingProgram?.id ?? generateId(),
        name: name.trim(),
        days,
        createdAt: existingProgram?.createdAt ?? new Date().toISOString(),
        coachId: profile.id,
        assignedTo: existingProgram?.assignedTo ?? [],
      };
      await saveProgram(program);
      onSaved();
      onClose();
    } catch (err) {
      console.warn('Save program error:', err);
      const msg = 'Failed to save program.';
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible animationType="slide" onRequestClose={onClose}>
      <View style={[styles.container, { backgroundColor: c.background }]}>
        {/* Header */}
        <View style={[styles.header, { borderBottomColor: c.border }]}>
          <TouchableOpacity onPress={onClose}>
            <X size={22} color={c.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: c.text }]}>
            {existingProgram ? 'Edit Program' : 'New Program'}
          </Text>
          <TouchableOpacity onPress={handleSave} disabled={saving}>
            <Text style={[styles.saveBtn, { color: c.accent, opacity: saving ? 0.5 : 1 }]}>
              {saving ? 'Saving...' : 'Save'}
            </Text>
          </TouchableOpacity>
        </View>

        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
        <ScrollView contentContainerStyle={styles.content}>
          {/* Program Name */}
          <TextInput
            style={[styles.nameInput, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
            placeholder="Program Name (e.g. 4-Day Upper/Lower)"
            placeholderTextColor={c.textTertiary}
            value={name}
            onChangeText={setName}
          />

          {/* Days */}
          {days.map((day, dayIdx) => (
            <View key={day.id} style={[styles.dayCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <View style={styles.dayHeader}>
                <TextInput
                  style={[styles.dayNameInput, { color: c.text, borderColor: c.border }]}
                  value={day.name}
                  onChangeText={(v) => updateDayName(day.id, v)}
                  placeholder="Day name"
                  placeholderTextColor={c.textTertiary}
                />
                {days.length > 1 && (
                  <TouchableOpacity onPress={() => removeDay(day.id)}>
                    <Trash2 size={18} color={c.textTertiary} />
                  </TouchableOpacity>
                )}
              </View>

              {/* Exercises */}
              {day.exercises.map((ex, exIdx) => (
                <View key={ex.id} style={[styles.exerciseRow, { borderTopColor: c.border }]}>
                  <View style={styles.exerciseMain}>
                    {/* Exercise name with picker button */}
                    <View style={styles.exNameRow}>
                      <TextInput
                        style={[styles.exNameInput, { color: c.text, borderColor: c.border, flex: 1 }]}
                        placeholder="Exercise name"
                        placeholderTextColor={c.textTertiary}
                        value={ex.exerciseName}
                        onChangeText={(v) => updateExercise(day.id, ex.id, { exerciseName: v })}
                      />
                      <TouchableOpacity
                        style={[styles.pickerBtn, { backgroundColor: c.accent + '18', borderColor: c.accent + '40' }]}
                        onPress={() => setPickerTarget({ dayId: day.id, exId: ex.id })}>
                        <Search size={14} color={c.accent} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.targetRow}>
                      <TextInput
                        style={[styles.numInput, { color: c.text, borderColor: c.border }]}
                        placeholder="Sets"
                        placeholderTextColor={c.textTertiary}
                        keyboardType="numeric"
                        value={ex.targetSets ? String(ex.targetSets) : ''}
                        onChangeText={(v) => updateExercise(day.id, ex.id, { targetSets: parseInt(v) || 0 })}
                      />
                      <Text style={{ color: c.textSecondary, fontWeight: '700' }}>×</Text>
                      <TextInput
                        style={[styles.numInput, { color: c.text, borderColor: c.border }]}
                        placeholder="Reps"
                        placeholderTextColor={c.textTertiary}
                        keyboardType="numeric"
                        value={ex.targetReps ? String(ex.targetReps) : ''}
                        onChangeText={(v) => updateExercise(day.id, ex.id, { targetReps: parseInt(v) || 0 })}
                      />
                      <Text style={{ color: c.textSecondary, fontWeight: '700' }}>@</Text>
                      <TextInput
                        style={[styles.numInput, { color: c.text, borderColor: c.border, flex: 1.5 }]}
                        placeholder="Weight"
                        placeholderTextColor={c.textTertiary}
                        keyboardType="numeric"
                        value={ex.targetWeight ? String(ex.targetWeight) : ''}
                        onChangeText={(v) => updateExercise(day.id, ex.id, { targetWeight: parseInt(v) || undefined })}
                      />
                    </View>
                    {/* RPE Button Grid */}
                    <View style={styles.rpeRow}>
                      <Text style={[styles.rpeLabel, { color: c.textSecondary }]}>RPE</Text>
                      <View style={styles.rpeGrid}>
                        {RPE_VALUES.map((val) => {
                          const isSelected = ex.rpe === val;
                          return (
                            <TouchableOpacity
                              key={val}
                              style={[
                                styles.rpeGridBtn,
                                {
                                  backgroundColor: isSelected ? c.accent : 'transparent',
                                  borderColor: isSelected ? c.accent : c.border,
                                },
                              ]}
                              onPress={() => updateExercise(day.id, ex.id, { rpe: isSelected ? undefined : val })}
                            >
                              <Text style={[styles.rpeGridBtnText, { color: isSelected ? '#fff' : c.textSecondary }]}>
                                {val}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </View>
                    <TextInput
                      style={[styles.notesInput, { color: c.textSecondary, borderColor: c.border }]}
                      placeholder="Notes (optional)"
                      placeholderTextColor={c.textTertiary}
                      value={ex.notes ?? ''}
                      onChangeText={(v) => updateExercise(day.id, ex.id, { notes: v || undefined })}
                    />
                  </View>
                  <View style={styles.exerciseActions}>
                    <TouchableOpacity onPress={() => moveExercise(day.id, ex.id, -1)} disabled={exIdx === 0}>
                      <ChevronUp size={18} color={exIdx === 0 ? c.border : c.textSecondary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => moveExercise(day.id, ex.id, 1)} disabled={exIdx === day.exercises.length - 1}>
                      <ChevronDown size={18} color={exIdx === day.exercises.length - 1 ? c.border : c.textSecondary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => removeExercise(day.id, ex.id)}>
                      <Trash2 size={16} color={c.textTertiary} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}

              <TouchableOpacity style={[styles.addExBtn, { borderColor: c.border }]} onPress={() => addExercise(day.id)}>
                <Plus size={16} color={c.accent} />
                <Text style={{ color: c.accent, fontWeight: '600', fontSize: 13 }}>Add Exercise</Text>
              </TouchableOpacity>
            </View>
          ))}

          <TouchableOpacity style={[styles.addDayBtn, { backgroundColor: c.surface, borderColor: c.accent }]} onPress={addDay}>
            <Plus size={18} color={c.accent} />
            <Text style={{ color: c.accent, fontWeight: '700', fontSize: 15 }}>Add Day</Text>
          </TouchableOpacity>
        </ScrollView>
        </KeyboardAvoidingView>
      </View>

      {/* Exercise Picker Modal */}
      {pickerTarget && (
        <MobileExercisePicker
          visible
          exercises={exerciseList}
          onClose={() => setPickerTarget(null)}
          onSelectExercise={(exercise) => {
            updateExercise(pickerTarget.dayId, pickerTarget.exId, { exerciseName: exercise.name });
            setPickerTarget(null);
          }}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  headerTitle: { fontSize: 18, fontWeight: '800' },
  saveBtn: { fontSize: 16, fontWeight: '700' },
  content: { padding: 16, paddingBottom: 60, gap: 16 },
  nameInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    fontWeight: '700',
  },
  dayCard: { borderWidth: 1, borderRadius: 2, overflow: 'hidden' },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    gap: 8,
  },
  dayNameInput: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    borderBottomWidth: 1,
    paddingBottom: 4,
  },
  exerciseRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    padding: 12,
    gap: 8,
  },
  exerciseMain: { flex: 1, gap: 8 },
  exNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  exNameInput: {
    fontSize: 14,
    fontWeight: '600',
    borderBottomWidth: 1,
    paddingBottom: 4,
  },
  pickerBtn: {
    width: 30,
    height: 30,
    borderRadius: 2,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  targetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  numInput: {
    borderBottomWidth: 1,
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
    minWidth: 40,
  },
  rpeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  rpeLabel: {
    fontSize: 12,
    fontWeight: '700',
    width: 28,
  },
  rpeGrid: {
    flexDirection: 'row',
    gap: 4,
    flex: 1,
  },
  rpeGridBtn: {
    flex: 1,
    height: 28,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rpeGridBtnText: {
    fontSize: 12,
    fontWeight: '700',
  },
  notesInput: {
    fontSize: 12,
    borderBottomWidth: 1,
    paddingBottom: 4,
  },
  exerciseActions: {
    alignItems: 'center',
    gap: 6,
    paddingTop: 4,
  },
  addExBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: 10,
    borderTopWidth: 1,
  },
  addDayBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 14,
    borderRadius: 2,
    borderWidth: 1,
  },
});
